// src/app/services/condition.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  ConditionEntity,
  RawConditionData,
  Condition,
} from '@models/condition.models';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class ConditionService {
  private readonly apiBase = environment.apiBaseUrl ?? '';
  private readonly conditionsBaseUrl = `${this.apiBase}/api/conditions`;
  private readonly adminConditionsBaseUrl = `${this.apiBase}/api/admin/conditions`;
  private readonly adminConditionDetailsBaseUrl = `${this.apiBase}/api/admin/condition-details`;
  private readonly adminDifferentialFindingsBaseUrl = `${this.apiBase}/api/admin/differential-findings`;

  private readonly useStatic = !!environment.useStaticContent;
  private readonly staticBase = environment.staticBasePath ?? '/assets/v1';

  constructor(private http: HttpClient) {}

  getAllConditions(): Observable<ConditionEntity[]> {
    if (this.useStatic) {
      return this.http
        .get<string[]>(`${this.staticBase}/slugs.json`)
        .pipe(
          map((slugs) =>
            (slugs ?? []).map(
              (s) => ({ id: s, slug: s }) as unknown as ConditionEntity,
            ),
          ),
        );
    }
    return this.http.get<ConditionEntity[]>(this.conditionsBaseUrl);
  }

  getConditionById(id: string): Observable<RawConditionData> {
    if (this.useStatic) {
      if (this.apiBase) {
        return this.http.get<RawConditionData>(
          `${this.conditionsBaseUrl}/id/${id}`,
        );
      }
      return throwError(
        () => new Error('Static mode: get by id not supported'),
      );
    }
    return this.http.get<RawConditionData>(
      `${this.conditionsBaseUrl}/id/${id}`,
    );
  }

  /**
   * Module is REQUIRED.
   * NOTE: This still calls the slug endpoint (if that's what your backend exposes),
   * but enforces NOT FOUND + module mismatch in the client.
   */
  getConditionBySlug(
    module: string,
    slug: string,
  ): Observable<RawConditionData> {
    const requestedModule = (module ?? '').trim();
    const requestedSlug = (slug ?? '').trim();

    if (!requestedModule || !requestedSlug) {
      return throwError(() => new Error('Entry not found'));
    }

    if (this.useStatic) {
      return this.http
        .get(
          `${this.staticBase}/conditions/${encodeURIComponent(requestedSlug)}.json`,
        )
        .pipe(
          map(this.adaptBundle),
          map((raw) =>
            this.validateEntryOrThrow(raw, requestedModule, requestedSlug),
          ),
          tap((res) => {
            console.groupCollapsed(
              '[Condition API][static]',
              `${requestedModule}/${requestedSlug}`,
            );
            console.log('condition:', res?.condition);
            console.log(
              'condition.seo_description:',
              (res as any)?.condition?.seo_description,
            );
            console.log(
              'details[0].condition_seo_description:',
              (res as any)?.details?.[0]?.condition_seo_description,
            );
            console.log(
              'taxonomy len:',
              res?.taxonomy?.length ?? 0,
              'details len:',
              res?.details?.length ?? 0,
            );
            console.groupEnd();
          }),
        );
    }

    return this.http
      .get<RawConditionData>(
        `${this.conditionsBaseUrl}/slug/${encodeURIComponent(requestedSlug)}`,
      )
      .pipe(
        map((raw) =>
          this.validateEntryOrThrow(raw, requestedModule, requestedSlug),
        ),
        tap((res) => {
          console.groupCollapsed(
            '[Condition API][live]',
            `${requestedModule}/${requestedSlug}`,
          );
          console.log('condition:', res?.condition);
          console.log(
            'condition.seo_description:',
            (res as any)?.condition?.seo_description,
          );
          console.log(
            'details[0].condition_seo_description:',
            (res as any)?.details?.[0]?.condition_seo_description,
          );
          console.log(
            'taxonomy len:',
            res?.taxonomy?.length ?? 0,
            'details len:',
            res?.details?.length ?? 0,
          );
          console.groupEnd();
        }),
      );
  }

  /**
   * Convert "empty/safe bundle" into an error so your component can redirect to /404.
   * Also enforces module mismatch when the API provides a module slug.
   */
  private validateEntryOrThrow(
    raw: RawConditionData,
    requestedModule: string,
    requestedSlug: string,
  ): RawConditionData {
    const cond = (raw as any)?.condition;

    const id = typeof cond?.id === 'string' ? cond.id : '';
    const slug = typeof cond?.slug === 'string' ? cond.slug : '';

    // Key fix: invalid slug must be treated as NOT FOUND.
    if (!id || !slug) {
      throw new Error('Entry not found');
    }

    // Optional but useful sanity check.
    if (slug !== requestedSlug) {
      throw new Error('Entry slug mismatch');
    }

    const moduleFromApi = this.extractModuleSlug(raw);
    if (moduleFromApi && moduleFromApi !== requestedModule) {
      throw new Error('Entry module mismatch');
    }

    return raw;
  }

  private extractModuleSlug(raw: RawConditionData): string | null {
    const cond = (raw as any)?.condition;

    const direct =
      (typeof cond?.module_slug === 'string' && cond.module_slug) ||
      (typeof cond?.moduleSlug === 'string' && cond.moduleSlug) ||
      (typeof cond?.module === 'string' && cond.module) ||
      (typeof cond?.module?.slug === 'string' && cond.module.slug) ||
      (typeof (raw as any)?.module_slug === 'string' &&
        (raw as any).module_slug) ||
      (typeof (raw as any)?.moduleSlug === 'string' && (raw as any).moduleSlug);

    if (direct) return direct;

    const taxonomy0 = Array.isArray((raw as any)?.taxonomy)
      ? (raw as any).taxonomy[0]
      : null;
    const fromTax =
      (typeof taxonomy0?.module_slug === 'string' && taxonomy0.module_slug) ||
      (typeof taxonomy0?.moduleSlug === 'string' && taxonomy0.moduleSlug) ||
      (typeof taxonomy0?.module === 'string' && taxonomy0.module) ||
      (typeof taxonomy0?.module?.slug === 'string' && taxonomy0.module.slug);

    return fromTax || null;
  }

  // Normalize static bundle shape and backfill seo_description if only present on detail rows.
  private adaptBundle = (bundle: any): RawConditionData => {
    const c = bundle?.condition ?? {};
    const taxonomy = Array.isArray(bundle?.taxonomy) ? bundle.taxonomy : [];
    const details = Array.isArray(bundle?.details) ? bundle.details : [];
    const differentials = Array.isArray(bundle?.differentials)
      ? bundle.differentials
      : [];

    const safeTaxonomy = (taxonomy.length ? taxonomy : [{}]).map((t: any) => ({
      path_segments: t?.path_segments ?? [],
      ...t,
    }));

    const safeCondition: any = { path_segments: c?.path_segments ?? [], ...c };
    if (
      !safeCondition.seo_description &&
      details?.[0]?.condition_seo_description
    ) {
      safeCondition.seo_description = details[0].condition_seo_description;
    }

    return {
      schema_version: bundle?.schema_version ?? '1.0',
      generated_at: bundle?.generated_at ?? new Date().toISOString(),
      condition: safeCondition,
      taxonomy: safeTaxonomy,
      details,
      differentials,
    } as RawConditionData;
  };

  // -------- Admin writes (unchanged) --------

  createCondition(condition: Partial<Condition>): Observable<Condition> {
    return this.http.post<Condition>(this.adminConditionsBaseUrl, condition);
  }

  updateCondition(
    id: string,
    condition: Partial<Condition>,
  ): Observable<Condition> {
    return this.http.put<Condition>(
      `${this.adminConditionsBaseUrl}/${id}`,
      condition,
    );
  }

  deleteCondition(id: string, deleteEntry = false): Observable<void> {
    const params = { deleteEntry: deleteEntry.toString() };
    return this.http.delete<void>(`${this.adminConditionsBaseUrl}/${id}`, {
      params,
    });
  }

  setConditionDetailsReviewFlag(detailId: string, flag: boolean) {
    return this.http.post(
      `${this.adminConditionDetailsBaseUrl}/${detailId}/review-flag`,
      { flag },
    );
  }

  setConditionDetailsUpdateFlag(detailId: string, flag: boolean) {
    return this.http.post(
      `${this.adminConditionDetailsBaseUrl}/${detailId}/update-flag`,
      { flag },
    );
  }

  setDifferentialReviewFlag(findingId: string, flag: boolean) {
    return this.http.post(
      `${this.adminDifferentialFindingsBaseUrl}/${findingId}/review-flag`,
      { flag },
    );
  }

  setDifferentialUpdateFlag(findingId: string, flag: boolean) {
    return this.http.post(
      `${this.adminDifferentialFindingsBaseUrl}/${findingId}/update-flag`,
      { flag },
    );
  }

  setConditionReviewFlag(detailId: string, flag: boolean) {
    return this.http.post(
      `${this.adminConditionsBaseUrl}/${detailId}/review-flag`,
      { flag },
    );
  }

  setConditionUpdateFlag(detailId: string, flag: boolean) {
    return this.http.post(
      `${this.adminConditionsBaseUrl}/${detailId}/update-flag`,
      { flag },
    );
  }
}
